// src/forgot/ResetPassword.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AuthShell, { AUTH_BRAND_BLUE as BLUE } from "../auth/AuthShell";
import {
  fadeUp,
  hoverRaise,
  staggerContainer,
} from "../Animations/motionpresets";
import LockIcon from "../Icons/LockIcon.jsx";
import EmailIcon from "../Icons/EmailIcon.jsx";
import PasswordCheckListLogin from "../auth/PasswordCheckListLogin.jsx";
import { evaluatePassword } from "../utils/passwordRules.js";

/* ---------- API ---------- */
async function confirmPasswordReset({ email, token, new_password }) {
  const res = await fetch(
    "https://towing-portal-api.onrender.com/api/v1/auth/password-reset/confirm",
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, token, new_password }),
    }
  );
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  if (!res.ok) {
    // Surface server message when possible
    throw new Error(
      data?.message || data?.error || text || `Request failed (${res.status})`
    );
  }
  return data || { message: "Password reset successfully." };
}

/* Small helpers for the UI */
const brandCircleStyle = {
  background: "linear-gradient(135deg,#2A7EFA,#5AA9FF)",
};

export default function ResetPassword() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // token from URL
  const token = useMemo(
    () => (searchParams.get("token") || "").trim(),
    [searchParams]
  );

  // fields
  const [email, setEmail] = useState(
    () => localStorage.getItem("last_reset_email") || ""
  );
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");

  // ui state
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // show/hide toggles
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);

  // Guard: token must exist
  const missingToken = !token;

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!email.trim() || !email.includes("@")) {
      setErr("Enter a valid email address.");
      return;
    }
    const { missing } = evaluatePassword(pass);
    if (missing.length > 0) {
      setErr("Please meet the password requirements.");
      return;
    }
    if (pass !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    try {
      setLoading(true);
      const resp = await confirmPasswordReset({
        email: email.trim(),
        token,
        new_password: pass,
      });
      setSuccessMsg(resp?.message || "Password reset successfully.");
      // Clear the stored hint so next time is clean
      localStorage.removeItem("last_reset_email");
    } catch (e2) {
      setErr(e2.message || "Could not reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <motion.div
        variants={staggerContainer(0.12, 0.18)}
        initial="hidden"
        animate="show"
      >
        {/* Brand header */}
        <motion.div className="mb-4 flex items-center gap-2" variants={fadeUp}>
          <span
            className="inline-grid place-items-center h-8 w-8 rounded-full text-white ring-1 ring-white/50"
            style={brandCircleStyle}
          >
            <EmailIcon className="w-5 h-5" />
          </span>
          <span className="text-sm font-semibold text-slate-900">
            Towing Portal
          </span>
        </motion.div>

        {/* Title */}
        <motion.div className="text-left" variants={fadeUp}>
          <div className="mb-1 text-[15px] font-bold text-black">
            Create new password
          </div>
          <h1
            className="text-2xl font-extrabold leading-tight"
            style={{ color: BLUE }}
          >
            Set a strong password
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Use a unique password you don’t use elsewhere.
          </p>
        </motion.div>

        <AnimatePresence initial={false} mode="wait">
          {missingToken ? (
            /* Missing / malformed token */
            <motion.div
              key="no-token"
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/90 p-4 text-amber-800"
            >
              <div className="font-semibold mb-1">Invalid or expired link</div>
              <p className="text-sm">
                Your reset link is missing or invalid. Request a new one and try
                again.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => nav("/forgot")}
                  className="rounded-lg px-3 py-2 text-white"
                  style={{
                    background: BLUE,
                    boxShadow: `0 8px 20px ${BLUE}33`,
                  }}
                >
                  Request new link
                </button>
              </div>
            </motion.div>
          ) : successMsg ? (
            /* Success state */
            <motion.div
              key="success"
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 text-emerald-800"
            >
              <div className="font-semibold mb-1">{successMsg}</div>
              <p className="text-sm">
                You can now sign in with your new password.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => nav("/login")}
                  className="rounded-lg px-3 py-2 text-white"
                  style={{
                    background: BLUE,
                    boxShadow: `0 8px 20px ${BLUE}33`,
                  }}
                >
                  Back to login
                </button>
              </div>
            </motion.div>
          ) : (
            /* Form */
            <motion.form
              key="form"
              onSubmit={onSubmit}
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5"
            >
              {/* Email */}
              <motion.label
                className="block text-sm text-slate-700"
                variants={fadeUp}
              >
                <span className="font-bold">Email</span>
                <motion.div className="relative mt-2 group" {...hoverRaise}>
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-2
                               text-slate-900 placeholder-slate-400 outline-none
                               focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60 disabled:opacity-70"
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                    <EmailIcon className="w-5 h-5" />
                  </span>
                </motion.div>
              </motion.label>

              {/* New password */}
              <motion.label
                className="block mt-4 text-sm text-slate-700"
                variants={fadeUp}
              >
                <span className="font-bold">New password</span>
                <motion.div className="relative mt-2 group" {...hoverRaise}>
                  <input
                    type={show1 ? "text" : "password"}
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-12 py-2
                               text-slate-900 placeholder-slate-400 outline-none
                               focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60"
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                    <LockIcon className="w-5 h-5" />
                  </span>
                  <button
                    type="button"
                    onClick={() => setShow1((s) => !s)}
                    aria-label={show1 ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center rounded-md px-2 py-1 text-slate-500 hover:text-slate-700"
                  >
                    {show1 ? eyeOpen : eyeClosed}
                  </button>
                </motion.div>
              </motion.label>

              {/* Checklist */}
              <PasswordCheckListLogin
                value={pass}
                show={pass.length > 0}
                defaultCollapsed
              />

              {/* Confirm password */}
              <motion.label
                className="block mt-4 text-sm text-slate-700"
                variants={fadeUp}
              >
                <span className="font-bold">Confirm password</span>
                <motion.div className="relative mt-2 group" {...hoverRaise}>
                  <input
                    type={show2 ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-12 py-2
                               text-slate-900 placeholder-slate-400 outline-none
                               focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60"
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                    <LockIcon className="w-5 h-5" />
                  </span>
                  <button
                    type="button"
                    onClick={() => setShow2((s) => !s)}
                    aria-label={show2 ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center rounded-md px-2 py-1 text-slate-500 hover:text-slate-700"
                  >
                    {show2 ? eyeOpen : eyeClosed}
                  </button>
                </motion.div>
              </motion.label>

              {/* Errors */}
              <AnimatePresence>
                {err && (
                  <motion.div
                    key="err"
                    initial={{ y: -6, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -6, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 26 }}
                    className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm"
                  >
                    {err}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit */}
              <motion.button
                variants={fadeUp}
                type="submit"
                disabled={loading}
                style={{ background: BLUE, boxShadow: `0 12px 28px ${BLUE}33` }}
                className="mt-4 w-full rounded-xl py-2.5 font-semibold text-white cursor-pointer transition
                           hover:brightness-[1.04] active:translate-y-px disabled:opacity-70"
              >
                {loading ? "Saving…" : "Save password"}
              </motion.button>

              <motion.div
                variants={fadeUp}
                className="mt-4 text-center text-xs"
              >
                <button
                  type="button"
                  onClick={() => nav("/login")}
                  className="text-sky-600 hover:underline"
                >
                  Back to login
                </button>
              </motion.div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Footer note */}
        <motion.div
          variants={fadeUp}
          className="mt-5 pt-3 border-t border-slate-200 text-[11px] text-slate-500 text-left"
        >
          We’ll never email you links asking for your password.
        </motion.div>
      </motion.div>
    </AuthShell>
  );
}

/* inline icons for show/hide */
const eyeOpen = (
  <svg className="h-5 w-5 cursor-pointer" viewBox="0 0 24 24" fill="none">
    <path
      d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const eyeClosed = (
  <svg className="h-5 w-5 cursor-pointer" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 3l18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M10.58 10.72a3 3 0 004.12 4.12M9.88 5.09A9.76 9.76 0 0121 12c-1.8 3.6-5.4 6-9 6-1.34 0-2.63-.28-3.8-.8M6.18 6.18A9.76 9.76 0 003 12c1.8 3.6 5.4 6 9 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
